/**
 * Prompts for the AI Pricing Assistant (Phase 2H).
 *
 * The numbers themselves are produced deterministically by the
 * `ai-pricing-optimizer` statistics endpoint. The model NEVER invents a price:
 * it explains the statistics it is given and turns them into advice, which is
 * why every prompt below repeats that constraint.
 *
 * Prompt engineering is a product concern — no vendor names, no transport
 * details, no model ids in this file.
 */

import type { PricingMarketStats } from '../types';

export const PRICING_LIMITS = {
  /** Output budget: a short summary plus up to three tips. */
  maxOutputTokens: 500,
  maxSummaryChars: 600,
  maxTipChars: 200,
  maxTips: 3,
} as const;

const money = (value: number | null | undefined, currency: string): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'unknown'
    : `${currency} ${value.toFixed(2)}`;

const percent = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'unknown'
    : `${Math.round(value * 100)}%`;

export interface PricingPromptInput {
  stats: PricingMarketStats;
  currency: string;
  language: string;
  location?: string;
  capacity?: number | null;
}

export function pricingSystemPrompt(input: PricingPromptInput): string {
  return [
    'You are a ticket pricing advisor for an event platform.',
    'You are given pre-computed market statistics. Treat them as ground truth.',
    'NEVER invent, recompute or contradict a number: only reference figures present in the data.',
    'Never mention or identify individual competing events, organizers or venues.',
    `Write in the language with code "${input.language}".`,
    `All money amounts use the currency code "${input.currency}".`,
    'Respond with a JSON object exactly of the form:',
    '{"summary": string, "tips": string[]}',
    `"summary" is at most two sentences. "tips" holds up to ${PRICING_LIMITS.maxTips} short, concrete, actionable items.`,
    'Do not wrap the JSON in code fences and do not add any other keys.',
  ].join(' ');
}

export function pricingUserPrompt(input: PricingPromptInput): string {
  const { stats, currency } = input;
  const lines = [
    `Sample size: ${stats.sampleSize} comparable published events (${stats.eventsWithSales} of them with recorded sales).`,
    `Average price: ${money(stats.avgPrice, currency)}.`,
    `Median price: ${money(stats.medianPrice, currency)}.`,
    `Observed price range: ${money(stats.minPrice, currency)} – ${money(stats.maxPrice, currency)}.`,
    `Recommended price computed by the platform: ${money(stats.recommendedPrice, currency)}.`,
    stats.priceRange
      ? `Suggested working range: ${money(stats.priceRange.min, currency)} – ${money(stats.priceRange.max, currency)}.`
      : 'Suggested working range: unavailable.',
    `Average sell-through of comparable events: ${percent(stats.avgSellThrough)}.`,
    `Location adjustment factor: ${stats.locationFactor.toFixed(2)}; capacity adjustment factor: ${stats.capacityFactor.toFixed(2)}.`,
    `Statistical confidence: ${stats.confidence}.`,
    `Organizer location: ${input.location || 'not specified'}.`,
    `Planned capacity: ${input.capacity ?? 'not specified'}.`,
  ];

  if (stats.insufficientSample) {
    lines.push(
      'IMPORTANT: the comparable sample is too small to publish exact market figures. Do not state any specific competitor price. Give general pricing guidance and explain that the market data is thin.',
    );
  }

  return lines.join('\n');
}
